'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { Field, ImageField } from '@sitecore-content-sdk/nextjs';
import { Content } from '.generated/Content/ConveyancingCalculator.model';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { DM_Serif_Display, Source_Sans_3 } from 'next/font/google';

const dmSerifDisplay = DM_Serif_Display({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-dm-serif-display',
  weight: ['400'],
});

const sourceSansPro = Source_Sans_3({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-source-sans-pro',
  weight: ['400', '500', '600', '700'],
});

type AdditionalFeesBlock = {
  title: Field<string>;
  description: Field<string>;
  additionalFeesList: Content.ConveyancingCalculator.GenericFeeSlab_Item[];
};

export type PrintFeesTabData = {
  step2FeesExclVat?: FeeSection;
  step2MandatoryFees?: FeeSection;
  totalObj?: {
    label: string;
    amount: number | string;
  };
  tabTitle?: Field<string>;
  additionalFeesBlock?: AdditionalFeesBlock;
};

export type PrintFeesProps = {
  step2FeesExclVat?: FeeSection;
  step2MandatoryFees?: FeeSection;
  totalObj?: {
    label: string;
    amount: number | string;
  };
  // Use the same shape as DynamicForm to stay compatible with Sitecore form data
  fields?: CustomForm.Form['fields'];
  tabTitle?: Field<string>;
  additionalFeesBlock?: AdditionalFeesBlock;
  // Optional heading/subheading (since Sitecore fields may not exist here)
  headingText?: string;
  subheadingText?: string;
  guidanceBlock?: {
    title?: Field<string>;
    description?: Field<string>;
  };
  printDescription?: Field<string>;
  printLogoIcon?: ImageField;
  printTitle?: Field<string>;
  // Array of all tabs data for rendering all tabs at once
  tabsData?: PrintFeesTabData[];
};

type FeeItem = {
  label: string;
  amount: number | string;
  isTotal?: boolean;
  feeTooltipTitle?: Field<string>;
  feeTooltip?: Field<string>;
};

type FeeSection = {
  title: string;
  items: FeeItem[];
};

// Format currency value
const formatCurrency = (value: number | string): string => {
  if (typeof value === 'string') {
    // Extract number from string, format it, then replace in original string
    const match = value.match(/-?\d*\.?\d+/);
    if (match) {
      const num = parseFloat(match[0]);
      // Use the formatting from below
      const formattedNum = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      }).format(num);
      // Replace first occurrence of number in string with formatted number
      return value.replace(match[0], formattedNum);
    }
    return value;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(value);
};

/**
 * Replaces tokens in text with data from session storage for a specific tab
 * @param text - The text containing tokens like {{Service}}, {{Property}}, etc.
 * @param tabData - Optional tab-specific data to use for token replacement
 * @returns The text with tokens replaced, or original text if no session storage data
 */
const replaceTokens = (
  text: string | undefined,
  tabData?: PrintFeesTabData & { serviceType?: 'buy' | 'sell' }
): string => {
  if (!text || typeof window === 'undefined') {
    return text || '';
  }

  try {
    // Get form data from session storage at index 0
    const formDataStr = window.sessionStorage.getItem('conveyancing-form-data');
    if (!formDataStr) {
      return text;
    }

    const allFormData: Record<number, Record<string, string>> = JSON.parse(formDataStr);
    const formData = allFormData[0];
    if (!formData) {
      return text;
    }

    // Get service type from session storage or use tab-specific service type
    const serviceType =
      tabData?.serviceType || window.sessionStorage.getItem('conveyancing-selected-option') || '';
    const normalizedServiceType = serviceType.toLowerCase();

    // Map service type to display name
    const serviceDisplayMap: Record<string, string> = {
      buy: 'buying',
      sell: 'selling',
      buyandsell: 'buy and sell',
      remortgage: 'remortgage',
    };
    const serviceDisplayName = serviceDisplayMap[normalizedServiceType] || serviceType;

    // Map tokens to field names based on service type
    const getFieldMapping = (): {
      propertyType: string;
      mortgage: string;
      amount: string;
      property?: string;
    } => {
      switch (normalizedServiceType) {
        case 'buy':
          return {
            propertyType: 'propertyTypeBuying',
            mortgage: 'mortgageBuying',
            amount: 'propertyAmountBuying',
            property: 'buying',
          };
        case 'sell':
          return {
            propertyType: 'propertyTypeSelling',
            mortgage: 'mortgageSelling',
            amount: 'propertyAmountSelling',
            property: 'selling',
          };
        case 'remortgage':
          return {
            propertyType: '',
            mortgage: '',
            amount: 'remortgageAmount',
            property: '',
          };
        case 'buyandsell':
          // For buy and sell, use tab-specific service type if provided
          if (tabData?.serviceType === 'buy') {
            return {
              propertyType: 'propertyTypeBuying',
              mortgage: 'mortgageBuying',
              amount: 'propertyAmountBuying',
              property: 'buying',
            };
          } else if (tabData?.serviceType === 'sell') {
            return {
              propertyType: 'propertyTypeSelling',
              mortgage: 'mortgageSelling',
              amount: 'propertyAmountSelling',
              property: 'selling',
            };
          }
          // Fallback: prefer buying fields, fallback to selling
          return {
            propertyType: formData.propertyTypeBuying
              ? 'propertyTypeBuying'
              : 'propertyTypeSelling',
            mortgage: formData.mortgageBuying ? 'mortgageBuying' : 'mortgageSelling',
            amount: formData.propertyAmountBuying
              ? 'propertyAmountBuying'
              : 'propertyAmountSelling',
            property: formData.propertyBuying ? 'buying' : 'selling',
          };
        default:
          return {
            propertyType: 'propertyTypeBuying',
            mortgage: 'mortgageBuying',
            amount: 'propertyAmountBuying',
            property: 'buying',
          };
      }
    };

    const fieldMapping = getFieldMapping();

    // Get values from form data
    const propertyType = formData[fieldMapping.propertyType] || '';
    const mortgageValue = formData[fieldMapping.mortgage] || '';
    // Use tab-specific amount if available, otherwise use form data
    const amount =
      tabData?.totalObj?.amount && typeof tabData.totalObj.amount === 'string'
        ? tabData.totalObj.amount.replace(/[£,]/g, '').trim()
        : formData[fieldMapping.amount] || '';
    const property = fieldMapping.property ? formData[fieldMapping.property] || '' : '';

    // Format mortgage value to readable text
    const mortgageDisplay =
      mortgageValue.toLowerCase().includes('yes') ||
      mortgageValue.toLowerCase().includes('with') ||
      mortgageValue.toLowerCase() === 'true'
        ? 'with an outstanding mortgage'
        : mortgageValue.toLowerCase().includes('no') ||
            mortgageValue.toLowerCase().includes('without') ||
            mortgageValue.toLowerCase() === 'false'
          ? 'without mortgage'
          : mortgageValue || '';

    // Format amount as currency if it's a number
    const formattedAmount = amount ? formatCurrency(amount) : '';

    // Replace tokens
    let result = text;
    result = result.replace(/\{\{Service\}\}/g, serviceDisplayName);
    result = result.replace(
      /\{\{Property\}\}/g,
      property ? (property?.toLowerCase() === 'undecided' ? 'house' : property?.toLowerCase()) : ''
    );
    result = result.replace(
      /\{\{PropertyType\}\}/g,
      propertyType
        ? propertyType?.toLowerCase() === "don't know"
          ? 'a freehold'
          : `a ${propertyType?.toLowerCase()}`
        : ''
    );
    result = result.replace(
      /\{\{WithOrWithoutMortgage\}\}/g,
      mortgageDisplay
        ? mortgageDisplay?.toLowerCase() === "don't know"
          ? 'without mortgage'
          : mortgageDisplay?.toLowerCase()
        : ''
    );
    result = result.replace(/\{\{Amount\}\}/g, formattedAmount?.toLowerCase());

    return result;
  } catch (error) {
    console.error('Failed to replace tokens:', error);
    return text;
  }
};

const PrintFees = (props: PrintFeesProps): React.JSX.Element => {
  const {
    additionalFeesBlock,
    step2FeesExclVat,
    step2MandatoryFees,
    totalObj,
    printDescription,
    printLogoIcon,
    printTitle,
    tabsData,
  }: PrintFeesProps = props;

  // Replace tokens in printDescription with data from session storage
  // Use useState and useEffect to prevent hydration mismatch (sessionStorage only available on client)
  const [processedDescription, setProcessedDescription] = useState(printDescription?.value || '');

  useEffect(() => {
    // Only process tokens on client side after mount
    if (typeof window !== 'undefined' && printDescription?.value) {
      // Check if service type is "buy and sell" and we have tabs data
      const serviceType = window.sessionStorage.getItem('conveyancing-selected-option') || '';
      const normalizedServiceType = serviceType.toLowerCase();

      if (normalizedServiceType === 'buyandsell' && tabsData && tabsData.length > 0) {
        // For "buy and sell", combine descriptions from all tabs
        const combinedDescriptions = tabsData
          .map((tabData, index) => {
            // Determine service type for this tab (first tab is buying, second is selling)
            const tabServiceType: 'buy' | 'sell' = index === 0 ? 'buy' : 'sell';
            let tabDescription = replaceTokens(printDescription?.value, {
              ...tabData,
              serviceType: tabServiceType,
            });
            // Remove "Our estimate based on" from second tab description onwards
            if (index > 0) {
              tabDescription = tabDescription.replace(/Our estimate based on\s*/gi, '').trim();
            }
            return tabDescription;
          })
          .filter((desc) => desc && desc.trim() !== '')
          .join(' and '); // Combine with 'and'

        setProcessedDescription(combinedDescriptions || printDescription?.value || '');
      } else {
        // For single service types, use existing logic
        setProcessedDescription(replaceTokens(printDescription?.value));
      }
    }
  }, [
    printDescription?.value,
    tabsData,
    additionalFeesBlock,
    step2FeesExclVat,
    step2MandatoryFees,
  ]);

  const renderFeeItem = (item: FeeItem, index: number) => {
    const isTotal = item?.isTotal;
    const RowComponent = isTotal ? 'feeRowTotal' : 'feeRow';
    const LabelComponent = isTotal ? 'feeLabelTotal' : 'feeLabel';
    const AmountComponent = isTotal ? 'feeAmountTotal' : 'feeAmount';

    return (
      <div key={index} className="feeItem">
        <style jsx>{`
          .feeItem {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-spacing-12, 12px);
          }
          .feeRow {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid var(--color-input-border, #666666);
            padding: var(--spacing-spacing-12, 12px) 0;
          }
          .feeRowTotal {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-spacing-12, 12px) 0;
          }
          .feeLabelContainer {
            display: flex;
            gap: var(--spacing-spacing-8, 8px);
            align-items: center;
          }
          .feeLabel {
            color: var(--color-core-color-1-Navy, #0c1b39);
          }
          .feeAmount {
            color: var(--color-core-color-1-Navy, #0c1b39);
          }
          .feeLabelTotal {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-weight: 700;
          }
          .feeAmountTotal {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-weight: 700;
          }
        `}</style>
        <div className={RowComponent}>
          <div className="feeLabelContainer">
            <span className={LabelComponent}>{item?.label}</span>
          </div>
          <div className={AmountComponent}>{formatCurrency(item?.amount ?? 0)}</div>
        </div>
      </div>
    );
  };

  const renderFeeSection = (feeSection: FeeSection) => (
    <div key={feeSection.title} className="feeSectionContainer">
      <style jsx>{`
        .feeSectionContainer {
          display: flex;
          flex-direction: column;
          gap: var(--padding-tight, 16px);
          page-break-inside: avoid;
        }
        .feeList {
          display: flex;
          flex-direction: column;
        }
        .feeSectionTitle {
          color: var(--component-card-item-title, #0c1b39);
          font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
          font-size: var(--typography-font-size-body-xlarge, 24px);
          font-style: normal;
          font-weight: 600;
          line-height: var(--typography-line-height-body-xlarge, 32px);
        }
      `}</style>
      <h3 className="feeSectionTitle">{feeSection.title}</h3>
      <div className="feeList">
        {feeSection.items
          .filter(Boolean)
          .map((item: FeeItem, index: number) => renderFeeItem(item, index))}
      </div>
    </div>
  );

  const additionalFeesBlockItems = useMemo(() => {
    return additionalFeesBlock?.additionalFeesList?.map((item) => ({
      label: item.fields?.feeLabel?.value ?? '',
      amount: item.fields?.amount?.value || item.fields?.feeVariableText?.value || 0,
    }));
  }, [additionalFeesBlock]);

  const renderAdditionalFeesBlock = () => {
    return (
      <div className="additionalFeesBlock">
        <style jsx>{`
          .additionalFeesBlock {
            display: flex;
            padding: var(--component-accordion-item-padding-y, 32px)
              var(--component-accordion-item-padding-x, 32px);
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-spacing-16, 16px);
            border-radius: var(--component-accordion-item-border-radius, 16px);
            border: var(--component-accordion-item-border-width, 1px) solid
              var(--component-accordion-item-border-active, #0c1b39);
            background: var(--component-accordion-item-surface-active, #fff);
            page-break-before: always;
            page-break-after: always;
            page-break-inside: avoid;
            margin-top: 40px;
          }
          .additionalFeesBlockTitleContainer {
            display: flex;
            flex-direction: column;
          }
          .additionalFeesBlockTitle {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
            font-size: var(--typography-font-size-body-large, 18px);
            font-style: normal;
            font-weight: 700;
            line-height: var(--typography-line-height-body-large, 26px);
          }
          .additionalFeesBlockDescription {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-weight: 400;
            font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
            font-size: var(--typography-font-size-body-medium, 16px);
            font-style: normal;
            font-weight: 400;
            line-height: var(--typography-line-height-body-medium, 22px);
          }
          .additionalFeesBlockItems {
            display: flex;
            flex-direction: column;
            width: 100%;
          }
        `}</style>
        <div className="additionalFeesBlockTitleContainer">
          <h3 className="additionalFeesBlockTitle">{additionalFeesBlock?.title?.value}</h3>
          <p className="additionalFeesBlockDescription">
            {additionalFeesBlock?.description?.value}
          </p>
        </div>
        <div className="additionalFeesBlockItems">
          {additionalFeesBlockItems?.map((item: FeeItem, index: number) =>
            renderFeeItem(item, index)
          )}
        </div>
      </div>
    );
  };

  const renderTabAdditionalFeesBlock = (tabData: PrintFeesTabData) => {
    if (!tabData.additionalFeesBlock) return null;
    return (
      <div className="additionalFeesBlock">
        <style jsx>{`
          .additionalFeesBlock {
            display: flex;
            padding: var(--component-accordion-item-padding-y, 32px)
              var(--component-accordion-item-padding-x, 32px);
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-spacing-16, 16px);
            border-radius: var(--component-accordion-item-border-radius, 16px);
            border: var(--component-accordion-item-border-width, 1px) solid
              var(--component-accordion-item-border-active, #0c1b39);
            background: var(--component-accordion-item-surface-active, #fff);
            margin-top: 20px;
          }
          .additionalFeesBlockTitleContainer {
            display: flex;
            flex-direction: column;
          }
          .additionalFeesBlockTitle {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
            font-size: var(--typography-font-size-body-large, 18px);
            font-style: normal;
            font-weight: 700;
            line-height: var(--typography-line-height-body-large, 26px);
          }
          .additionalFeesBlockDescription {
            color: var(--color-core-color-1-Navy, #0c1b39);
            font-weight: 400;
            font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
            font-size: var(--typography-font-size-body-medium, 16px);
            font-style: normal;
            font-weight: 400;
            line-height: var(--typography-line-height-body-medium, 22px);
          }
          .additionalFeesBlockItems {
            display: flex;
            flex-direction: column;
            width: 100%;
          }
        `}</style>
        <div className="additionalFeesBlockTitleContainer">
          <h3 className="additionalFeesBlockTitle">{tabData.additionalFeesBlock?.title?.value}</h3>
          <p className="additionalFeesBlockDescription">
            {tabData.additionalFeesBlock?.description?.value}
          </p>
        </div>
        <div className="additionalFeesBlockItems">
          {tabData.additionalFeesBlock?.additionalFeesList
            ?.map((item) => ({
              label: item.fields?.feeLabel?.value ?? '',
              amount: item.fields?.amount?.value || item.fields?.feeVariableText?.value || 0,
            }))
            ?.map((item: FeeItem, index: number) => renderFeeItem(item, index))}
        </div>
      </div>
    );
  };

  return (
    <div className="print-fees">
      <style jsx>{`
        .print-fees {
          display: none;
          padding: 0 var(--component-section-container-padding-x, 80px);
        }
        .print-fees-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .formContainer {
          display: flex;
          padding: var(--padding-more, 40px);
          flex-direction: column;
          align-items: flex-start;
          gap: 24px;
          align-self: stretch;
          border-radius: var(--general-border-radius-card, 16px)
            var(--general-border-radius-card, 16px) 16px 16px;
          background: var(--color-surface-white, #f8f6f1);
        }
        .formGrid {
          display: flex;
          flex-direction: column;
          gap: 24px;
          width: 100%;
        }
        .formCard {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .totalSection {
          display: flex;
          flex-direction: column;
        }
        .totalRow {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-spacing-12, 12px) 0;
        }
        .totalLabel {
          color: var(--color-core-color-1-Navy, #0c1b39);
          font-weight: 600;
          font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
          font-size: var(--typography-font-size-body-xlarge, 24px);
          font-style: normal;
          font-weight: 600;
          line-height: var(--typography-line-height-body-xlarge, 32px);
        }
        .totalAmount {
          color: var(--color-core-color-1-Navy, #0c1b39);
          font-weight: 600;
          font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
          font-size: var(--typography-font-size-body-xlarge, 24px);
          font-style: normal;
          font-weight: 600;
          line-height: var(--typography-line-height-body-xlarge, 32px);
        }
        .printHeader {
          display: flex;
          flex-direction: column;
          padding: 20px 0 20px 0;
        }
        .printHeaderTitle {
          display: flex;
          flex-direction: column;
          padding: var(--component-hero-content-padding-y, 32px)
            var(--component-hero-content-padding-x, 0);
          gap: 30px;
        }
        .printHeaderTitleText {
          color: var(--component-hero-title, #0c1b39);
          font-family: var(--typography-font-family-header, ${dmSerifDisplay.style.fontFamily});
          font-size: var(--typography-font-size-display, 58px);
          font-style: normal;
          font-weight: 400;
          line-height: var(--typography-line-height-display, 62px);
        }
        .printHeaderDescriptionText {
          color: var(--component-hero-body, #0c1b39);
          font-family: var(--typography-font-family-body, ${sourceSansPro.style.fontFamily});
          font-size: var(--typography-font-size-body-xlarge, 24px);
          font-style: normal;
          font-weight: 600;
          line-height: var(--typography-line-height-body-xlarge, 32px);
        }
        .printHeaderLogo {
          width: auto;
          height: auto;
          object-fit: contain;
          object-position: center;
          max-width: 100%;
          max-height: 100%;
        }
        .printHeaderLogoContainer {
          width: 100%;
          height: auto;
          max-width: 300px;
          margin: 0 auto;
        }
        @media (min-width: 1024px) {
          .form {
            width: 65.27%;
          }
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
          }
          .print-fees {
            display: block;
          }
        }
      `}</style>
      <div className="printHeader">
        <div className="printHeaderTitle">
          {printLogoIcon?.value?.src && (
            <div className="printHeaderLogoContainer">
              <img
                src={printLogoIcon?.value?.src as string}
                alt={printLogoIcon?.value?.alt as string}
                width={printLogoIcon?.value?.width as number}
                height={printLogoIcon?.value?.height as number}
                className="printHeaderLogo"
              />
            </div>
          )}
          <h1 className="printHeaderTitleText">{printTitle?.value}</h1>
          <p className="printHeaderDescriptionText">{processedDescription}</p>
        </div>
      </div>
      <div className="print-fees-content">
        {tabsData && tabsData.length > 0 ? (
          // Render all tabs data at once
          tabsData.map((tabData, tabIndex) => {
            return (
              <section
                key={`tab-${tabIndex}`}
                className="formContainer"
                style={tabIndex > 0 ? { pageBreakBefore: 'always', marginTop: '40px' } : {}}
              >
                <div className="formGrid">
                  <div className="form">
                    <div className="formCard">
                      {tabData.step2FeesExclVat && renderFeeSection(tabData.step2FeesExclVat)}
                      {tabData.step2MandatoryFees && renderFeeSection(tabData.step2MandatoryFees)}
                      {/* Total Section */}
                      {((tabData.step2FeesExclVat?.items?.length ?? 0) > 0 ||
                        (tabData.step2MandatoryFees?.items?.length ?? 0) > 0) && (
                        <div className="totalSection">
                          <div className="totalRow">
                            <span className="totalLabel">{tabData.totalObj?.label}</span>
                            <span className="totalAmount">
                              {formatCurrency(tabData.totalObj?.amount ?? 0)}
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Additional Fees Accordion */}
                      {tabData.additionalFeesBlock && renderTabAdditionalFeesBlock(tabData)}
                    </div>
                  </div>
                </div>
              </section>
            );
          })
        ) : (
          <section className="formContainer">
            <div className="formGrid">
              <div className="form">
                <div className="formCard">
                  {step2FeesExclVat && renderFeeSection(step2FeesExclVat)}
                  {step2MandatoryFees && renderFeeSection(step2MandatoryFees)}
                  {/* Total Section */}
                  {((step2FeesExclVat?.items?.length ?? 0) > 0 ||
                    (step2MandatoryFees?.items?.length ?? 0) > 0) && (
                    <div className="totalSection">
                      <div className="totalRow">
                        <span className="totalLabel">{totalObj?.label}</span>
                        <span className="totalAmount">{formatCurrency(totalObj?.amount ?? 0)}</span>
                      </div>
                    </div>
                  )}
                  {/* Additional Fees Accordion */}
                  {additionalFeesBlock && renderAdditionalFeesBlock()}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PrintFees;
